/**
 * Variant dropdown custom element.
 *
 * Opens and closes the list of one variant option rendered by
 * snippets/variant-dropdown-picker.liquid. Selection itself is not handled
 * here: each row is a label around a radio, so a click checks the radio and
 * its change event bubbles up to <variant-picker>, which fetches the variant
 * like it does for a button click.
 *
 * - The list opens in the page flow and scrolls to the selected row.
 * - A pick, Escape or a click outside closes it.
 */
class VariantDropdown extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.trigger?.addEventListener('click', () => (this.isOpen ? this.close() : this.open()), { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener('pointerdown', this.#onDocumentPointerdown, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @returns {HTMLButtonElement | null} */
  get trigger() {
    return this.querySelector('.variant-dropdown__trigger');
  }

  /** @returns {HTMLElement | null} */
  get list() {
    return this.querySelector('.variant-dropdown__list');
  }

  get isOpen() {
    return this.trigger?.getAttribute('aria-expanded') === 'true';
  }

  open() {
    const { trigger, list } = this;
    if (!trigger || !list) return;

    trigger.setAttribute('aria-expanded', 'true');
    list.hidden = false;

    // Open on the selected value rather than on the top of a long list.
    const selectedRow = list.querySelector('input:checked')?.closest('label');
    if (selectedRow instanceof HTMLElement) list.scrollTop = selectedRow.offsetTop;
  }

  /**
   * @param {{ focusTrigger?: boolean }} [options]
   */
  close({ focusTrigger = false } = {}) {
    const { trigger, list } = this;
    if (!trigger || !list || !this.isOpen) return;

    trigger.setAttribute('aria-expanded', 'false');
    list.hidden = true;
    if (focusTrigger) trigger.focus();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    // The picker re-renders the trigger once the variant is fetched; showing
    // the value now keeps the closed field from lagging behind the click.
    const value = this.querySelector('.variant-dropdown__trigger-value');
    if (value) value.textContent = input.value;

    this.close({ focusTrigger: true });
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    event.preventDefault();
    this.close({ focusTrigger: true });
  };

  /** @param {PointerEvent} event */
  #onDocumentPointerdown = (event) => {
    if (this.isOpen && event.target instanceof Node && !this.contains(event.target)) this.close();
  };
}

if (!customElements.get('variant-dropdown')) {
  customElements.define('variant-dropdown', VariantDropdown);
}
